/**
 * Storage adapter: localStorage on web and native (Capacitor).
 *
 * Android WebView persists localStorage across app kills because Capacitor enables
 * `setDomStorageEnabled(true)` by default. Using localStorage everywhere keeps reads
 * synchronous (no hydration step) and avoids pulling a native plugin for storage
 * we don't need.
 */

export function getSync(key: string): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
}

export async function get(key: string): Promise<string | null> {
  return getSync(key);
}

export async function set(key: string, value: string): Promise<void> {
  if (typeof window !== "undefined") window.localStorage.setItem(key, value);
}

export async function remove(key: string): Promise<void> {
  if (typeof window !== "undefined") window.localStorage.removeItem(key);
}

/**
 * No-op on both platforms now that we use synchronous localStorage. Kept as an
 * exported function so existing call sites in AuthContext don't need to be edited
 * and so future native-storage experiments have a single place to hook.
 */
export async function hydrateStorage(): Promise<void> {
  return;
}

export const storage = { hydrate: hydrateStorage, getSync, get, set, remove };
